import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw, Search, Wrench, Eye, EyeOff, Lock, Users, UserPlus, Trash2 } from "lucide-react";
import { http, api } from "@/lib/api";
import { loadFeatures } from "@/lib/features";

const GRANT_GROUPS = [["admin", "Admin"], ["internal_tester", "Internal tester"], ["specific_user", "Specific user"]];

const FILTERS = ["All", "Enabled", "Disabled", "Maintenance"];
const ACCESS_OPTIONS = [["off", "OFF"], ["admin_only", "Admin only"], ["internal_testers", "Internal testers"], ["all_users", "All users"], ["specific_users", "Specific users"]];
const PLAN_OPTIONS = [["free", "Free"], ["pro", "Pro"]];

const stateOf = (f) => (f.maintenance_mode ? "Maintenance" : f.enabled ? "Enabled" : "Disabled");

export default function FeatureControl() {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("All");
  const [openGrants, setOpenGrants] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/admin/features");
      setFeatures(data || []);
    } catch { toast.error("Could not load features."); }
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const patch = async (key, body) => {
    setSaving(key);
    try {
      const { data } = await http.patch(`/admin/features/${key}`, body);
      setFeatures((fs) => fs.map((f) => (f.feature_key === key ? data : f)));
      await loadFeatures(); // invalidate the public store so the live app updates
      toast.success("Feature updated successfully.");
    } catch { toast.error("Update failed."); }
    setSaving(null);
  };

  const shown = features.filter((f) => {
    const matchQ = !q || (f.feature_name || "").toLowerCase().includes(q.toLowerCase()) || (f.description || "").toLowerCase().includes(q.toLowerCase());
    const matchF = filter === "All" || stateOf(f) === filter;
    return matchQ && matchF;
  });

  return (
    <div data-testid="feature-control">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-black text-white">Feature Control</h2>
          <p className="text-xs text-[#A1A1AA]">Enable, disable or put any feature into maintenance — changes go live on the next reload.</p>
        </div>
        <button onClick={refresh} disabled={loading} data-testid="feature-control-refresh"
          className="flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-1.5 text-xs font-semibold text-[#A1A1AA] hover:text-white disabled:opacity-50">
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex flex-1 items-center gap-2 rounded-lg border border-white/10 bg-white/[0.02] px-3 py-1.5">
          <Search size={14} className="text-[#666]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="feature-search" placeholder="Search features…"
            className="w-full bg-transparent text-sm text-white outline-none placeholder:text-[#666]" />
        </div>
        <div className="flex gap-1">
          {FILTERS.map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`feature-filter-${f.toLowerCase()}`}
              className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold ${filter === f ? "bg-[#00F0FF] text-black" : "border border-white/10 text-[#A1A1AA] hover:text-white"}`}>{f}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#00F0FF]" /></div>
      ) : (
        <div className="space-y-2" data-testid="feature-list">
          {shown.length === 0 && <div className="py-12 text-center text-sm text-[#666]">No features match.</div>}
          {shown.map((f) => {
            const st = stateOf(f);
            const busy = saving === f.feature_key;
            return (
              <div key={f.feature_key} data-testid={`feature-row-${f.feature_key}`}
                className="flex flex-wrap items-center gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-3">
                <div className="min-w-[200px] flex-1">
                  <div className="flex items-center gap-2 text-sm font-bold text-white">
                    {f.feature_name}
                    {f.critical && <span title="Critical feature" className="flex items-center gap-0.5 rounded-full bg-white/10 px-1.5 py-0.5 text-[9px] font-semibold text-[#A1A1AA]"><Lock size={9} /> core</span>}
                  </div>
                  <div className="text-xs text-[#A1A1AA]">{f.description}</div>
                  <div className="mt-0.5 text-[10px] text-[#555]">Updated {f.updated_at ? new Date(f.updated_at).toLocaleString() : "—"}{f.updated_by ? ` · ${f.updated_by}` : ""}</div>
                </div>

                <span data-testid={`feature-state-${f.feature_key}`}
                  className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold ${st === "Enabled" ? "bg-[#22c55e]/15 text-[#22c55e]" : st === "Maintenance" ? "bg-[#FFC53D]/15 text-[#FFC53D]" : "bg-white/10 text-[#A1A1AA]"}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${st === "Enabled" ? "bg-[#22c55e]" : st === "Maintenance" ? "bg-[#FFC53D]" : "bg-[#A1A1AA]"}`} /> {st}
                </span>

                <select data-testid={`feature-access-${f.feature_key}`} value={f.access_mode || "all_users"} disabled={busy}
                  onChange={(e) => patch(f.feature_key, { access_mode: e.target.value })}
                  className="rounded-lg border border-white/15 bg-[#0A0A0A] px-2 py-1.5 text-xs text-white">
                  {ACCESS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>

                <select data-testid={`feature-plan-${f.feature_key}`} value={f.plan || "free"} disabled={busy}
                  onChange={(e) => patch(f.feature_key, { plan: e.target.value })}
                  className="rounded-lg border border-white/15 bg-[#0A0A0A] px-2 py-1.5 text-xs text-white">
                  {PLAN_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>

                <button onClick={() => patch(f.feature_key, { visible_to_users: !f.visible_to_users })} disabled={busy}
                  data-testid={`feature-visibility-${f.feature_key}`} title="Toggle user visibility"
                  className={`flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-xs font-semibold ${f.visible_to_users ? "border-white/15 text-white" : "border-white/10 text-[#A1A1AA]"}`}>
                  {f.visible_to_users ? <><Eye size={13} /> Visible</> : <><EyeOff size={13} /> Hidden</>}
                </button>

                <button onClick={() => patch(f.feature_key, { maintenance_mode: !f.maintenance_mode })} disabled={busy}
                  data-testid={`feature-maintenance-${f.feature_key}`} title="Toggle maintenance mode"
                  className={`flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-xs font-semibold ${f.maintenance_mode ? "border-[#FFC53D]/50 bg-[#FFC53D]/10 text-[#FFC53D]" : "border-white/15 text-[#A1A1AA] hover:text-white"}`}>
                  <Wrench size={13} /> Maintenance
                </button>

                <button onClick={() => patch(f.feature_key, { enabled: !f.enabled })} disabled={busy}
                  data-testid={`feature-toggle-${f.feature_key}`}
                  className={`flex min-w-[92px] items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold ${f.enabled ? "bg-white/10 text-white hover:bg-white/15" : "bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90"}`}>
                  {busy ? <Loader2 size={13} className="animate-spin" /> : f.enabled ? "Disable" : "Enable"}
                </button>

                <button onClick={() => setOpenGrants(openGrants === f.feature_key ? null : f.feature_key)}
                  data-testid={`feature-users-${f.feature_key}`} title="Add admins / testers / specific users"
                  className={`flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-xs font-semibold ${openGrants === f.feature_key ? "border-[#00F0FF]/50 bg-[#00F0FF]/10 text-[#00F0FF]" : "border-white/15 text-[#A1A1AA] hover:text-white"}`}>
                  <Users size={13} /> Users
                </button>

                {openGrants === f.feature_key && <GrantsManager featureKey={f.feature_key} />}

                {f.maintenance_mode && (
                  <input defaultValue={f.maintenance_message || ""} data-testid={`feature-message-${f.feature_key}`}
                    onBlur={(e) => { if (e.target.value !== (f.maintenance_message || "")) patch(f.feature_key, { maintenance_message: e.target.value }); }}
                    placeholder="Maintenance message shown to users…"
                    className="w-full rounded-lg border border-[#FFC53D]/30 bg-black/30 px-3 py-1.5 text-xs text-white outline-none focus:border-[#FFC53D]/60" />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function GrantsManager({ featureKey }) {
  const [grants, setGrants] = useState({});
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [group, setGroup] = useState("internal_tester");
  const [plan, setPlan] = useState("free");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.adminFeatureGrants(featureKey); setGrants(r.grants || {}); }
    catch { toast.error("Could not load users."); }
    setLoading(false);
  }, [featureKey]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    const e = email.trim().toLowerCase();
    if (!e || !e.includes("@")) { toast.error("Enter a valid email."); return; }
    setBusy(true);
    try {
      await api.adminFeatureGrantAdd(featureKey, { group, email: e, plan });
      toast.success(`Added ${e} as ${group.replace("_", " ")}`);
      setEmail(""); await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not add user."); }
    setBusy(false);
  };
  const remove = async (g, e) => {
    setBusy(true);
    try { await api.adminFeatureGrantRemove(featureKey, g, e); await load(); }
    catch { toast.error("Could not remove user."); }
    setBusy(false);
  };

  return (
    <div className="mt-1 w-full rounded-xl border border-[#00F0FF]/20 bg-[#00F0FF]/[0.03] p-3" data-testid={`grants-panel-${featureKey}`}>
      <div className="mb-2 text-xs font-bold text-white">Add users to this feature</div>
      <div className="mb-3 flex flex-wrap items-end gap-2">
        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@email.com" type="email"
          data-testid={`grant-email-${featureKey}`} onKeyDown={(e) => e.key === "Enter" && add()}
          className="min-w-[200px] flex-1 rounded-lg border border-white/15 bg-[#0A0A0A] px-3 py-1.5 text-xs text-white outline-none focus:border-[#00F0FF]/60" />
        <select value={group} onChange={(e) => setGroup(e.target.value)} data-testid={`grant-group-${featureKey}`}
          className="rounded-lg border border-white/15 bg-[#0A0A0A] px-2 py-1.5 text-xs text-white">
          {GRANT_GROUPS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={plan} onChange={(e) => setPlan(e.target.value)} data-testid={`grant-plan-${featureKey}`}
          className="rounded-lg border border-white/15 bg-[#0A0A0A] px-2 py-1.5 text-xs text-white">
          {PLAN_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <button onClick={add} disabled={busy} data-testid={`grant-add-${featureKey}`}
          className="flex items-center gap-1 rounded-lg bg-[#00F0FF] px-3 py-1.5 text-xs font-bold text-black hover:bg-[#00F0FF]/90 disabled:opacity-50">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <UserPlus size={13} />} Add
        </button>
      </div>
      {loading ? (
        <div className="py-3 text-center"><Loader2 size={14} className="mx-auto animate-spin text-[#00F0FF]" /></div>
      ) : (
        <div className="space-y-2">
          {GRANT_GROUPS.map(([g, label]) => {
            const list = grants[g] || [];
            return (
              <div key={g}>
                <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-[#666]">{label}s ({list.length})</div>
                {list.length === 0 ? <div className="text-[11px] text-[#555]">None</div> : (
                  <div className="flex flex-wrap gap-1.5">
                    {list.map((gr) => (
                      <span key={gr.email} data-testid={`grant-item-${featureKey}-${gr.email}`}
                        className="flex items-center gap-1.5 rounded-full border border-white/15 bg-black/30 px-2.5 py-1 text-[11px] text-white">
                        {gr.email}
                        <span className={`rounded px-1 text-[9px] font-bold ${gr.plan === "pro" ? "bg-[#a78bfa]/20 text-[#a78bfa]" : "bg-white/10 text-[#A1A1AA]"}`}>{(gr.plan || "free").toUpperCase()}</span>
                        {!gr.user_exists && <span title="No account yet" className="text-[9px] text-[#FFC53D]">pending</span>}
                        <button onClick={() => remove(g, gr.email)} data-testid={`grant-remove-${featureKey}-${gr.email}`} className="text-[#A1A1AA] hover:text-red-400"><Trash2 size={11} /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
